import ClientForm from "@/components/clients/ClientForm";
import MergeClientsDialog from "@/components/clients/MergeClientsDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { getClientPortalBase, setClientTenant } from "@/lib/clientPortalPaths";
import { PageRes } from "@/services/api";
import clientService from "@/services/client";
import companyService from "@/services/company";
import { Client } from "@/types/client";
import { LogIn, Merge, MoreHorizontal, Pencil, PlusCircle, Search } from "lucide-react";
import { useEffect, useState } from "react";

export default function Clients() {
  const { toast } = useToast();

  const [clients, setClients] = useState<Client[]>([]);
  const [totalClients, setTotalClients] = useState(0);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<"create" | "edit">("create");
  const [editingClient, setEditingClient] = useState<Client | undefined>(undefined);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeSurvivor, setMergeSurvivor] = useState<Client | undefined>(undefined);
  const [companyNameById, setCompanyNameById] = useState<Record<string, string>>({});

  useEffect(() => {
    companyService.getAll(undefined, 1, 200).then(([data]) => {
      const map: Record<string, string> = {};
      (data?.result || []).forEach((c) => {
        map[c._id] = c.name;
      });
      setCompanyNameById(map);
    });
  }, []);

  const fetchClients = async () => {
    setIsLoading(true);
    try {
      const [data, error] = await clientService.getAll(search, page, limit);

      if (error) {
        toast({
          title: "Erro",
          description: error.msg || "Erro ao carregar clientes",
          variant: "destructive",
        });
        return;
      }

      const clientsData = data as PageRes<Client>;
      setClients(clientsData.result || []);
      setTotalClients(clientsData.total ?? 0);
    } catch {
      toast({
        title: "Erro",
        description: "Falha ao carregar os clientes",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, [page, limit, search]);

  const handleCreateClient = () => {
    setFormMode("create");
    setEditingClient(undefined);
    setFormOpen(true);
  };

  const handleEditClient = (client: Client) => {
    setFormMode("edit");
    setEditingClient(client);
    setFormOpen(true);
  };

  const handleMergeClient = (client?: Client) => {
    setMergeSurvivor(client);
    setMergeOpen(true);
  };

  const handleImpersonateClient = async (client: Client) => {
    const [data, error] = await clientService.impersonate(client._id);
    if (error || !data?.accessToken) {
      toast({
        title: "Erro",
        description: error?.msg || "Não foi possível acessar como este cliente",
        variant: "destructive",
      });
      return;
    }
    localStorage.setItem("client_token", data.accessToken);
    localStorage.setItem("client_user", JSON.stringify(data.user));
    localStorage.setItem("client_impersonated", "1");
    setClientTenant(data.subdomain || null);
    window.location.href = `${getClientPortalBase()}/tickets`;
  };

  return (
    <>
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">Clientes</h1>
          <p className="mt-1 text-muted-foreground">
            Contatos com acesso ao portal do cliente nesta organização.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button variant="outline" onClick={() => handleMergeClient()}>
            <Merge className="h-4 w-4 mr-2" />
            Mesclar contatos
          </Button>
          <Button onClick={handleCreateClient}>
            <PlusCircle className="h-4 w-4 mr-2" />
            Novo Cliente
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Lista de Clientes</CardTitle>
          <CardDescription>
            Usuários com permissão de cliente nesta organização (acesso ao portal).
          </CardDescription>
          <div className="flex items-center mt-4">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Buscar clientes..."
                className="pl-8"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>E-mail</TableHead>
                <TableHead>Telefone</TableHead>
                <TableHead>Empresa</TableHead>
                <TableHead>Tags</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-24 text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {clients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-6 text-muted-foreground">
                    {isLoading
                      ? "Carregando clientes..."
                      : "Nenhum cliente encontrado"}
                  </TableCell>
                </TableRow>
              ) : (
                clients.map((client) => {
                  const companyNames = (client.companyMemberships || [])
                    .map((m) => companyNameById[m.companyId])
                    .filter((name): name is string => Boolean(name));
                  return (
                  <TableRow key={client._id}>
                    <TableCell className="font-medium">{client.name}</TableCell>
                    <TableCell>
                      {client.email || "—"}
                      {(client.emails?.length || 0) > 1 && (
                        <span className="text-muted-foreground"> +{client.emails.length - 1}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {client.phone || "—"}
                      {(client.phones?.length || 0) > 1 && (
                        <span className="text-muted-foreground"> +{client.phones.length - 1}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {companyNames.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {companyNames.map((name) => (
                            <Badge key={name} variant="outline" className="text-xs">
                              {name}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(client.tags || []).length > 0 ? (
                          (client.tags || []).map((t) => (
                            <Badge key={`${client._id}-${t}`} variant="outline" className="text-xs">
                              {t}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          client.status === "pending"
                            ? "outline"
                            : client.isActive
                              ? "default"
                              : "secondary"
                        }
                      >
                        {client.status === "pending"
                          ? "Pendente"
                          : client.isActive
                            ? "Ativo"
                            : "Inativo"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                            <span className="sr-only">Abrir menu</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Ações</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleEditClient(client)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Editar
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleMergeClient(client)}>
                            <Merge className="h-4 w-4 mr-2" />
                            Mesclar com outro contato...
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => void handleImpersonateClient(client)}>
                            <LogIn className="h-4 w-4 mr-2" />
                            Acessar como este cliente
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
        <CardFooter className="flex justify-between">
          <div className="text-sm text-muted-foreground">
            Mostrando {clients.length} de {totalClients} clientes
          </div>
          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(p - 1, 1))}
              disabled={page === 1 || isLoading}
            >
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => p + 1)}
              disabled={clients.length < limit || isLoading}
            >
              Próxima
            </Button>
          </div>
        </CardFooter>
      </Card>

      {formOpen && (
        <ClientForm
          open={formOpen}
          onClose={() => setFormOpen(false)}
          onSuccess={fetchClients}
          initialData={editingClient}
          mode={formMode}
        />
      )}

      {mergeOpen && (
        <MergeClientsDialog
          open={mergeOpen}
          onClose={() => setMergeOpen(false)}
          onSuccess={fetchClients}
          initialSurvivor={mergeSurvivor}
        />
      )}
    </>
  );
}
